import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Fld, money, fmtDate, inputCls } from "./shared";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });

export const CheckTypeCell = ({ r }) => {
  if (r.check_type !== "post_dated") return <span className="text-xs">Dated{r.account_name ? ` · ${r.account_name}` : ""}</span>;
  return (
    <div className="text-xs">
      <span className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">Post-dated</span>
      <div data-testid={`release-${r.number}`} className="text-slate-500 mt-1">
        {r.release ? `Released from ${r.release.account_name} by ${r.release.by_name}` : r.status === "Approved" ? "Awaiting release" : ""}
      </div>
    </div>
  );
};

export function ReleaseCheckDialog({ record, onDone }) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const due = (record.date || "").slice(0, 10) <= today();
  useEffect(() => { if (open) { api.get("/finance/cash/accounts").then((r) => setAccounts(r.data)).catch(() => {}); } }, [open]);
  const release = async () => {
    try {
      await api.post(`/finance/check-vouchers/${record.id}/release`, { account_id: accId });
      toast.success(`${record.number} released`); setOpen(false); onDone();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`button-release-${record.number}`} size="sm" className="bg-violet-600 hover:bg-violet-500"><Send size={13} className="mr-1" /> Release</Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>Release post-dated check {record.number}</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1 text-slate-300">
          <div>{record.payee} · {record.bank} #{record.check_number}</div>
          <div>Check date <b>{fmtDate(record.date)}</b> · <span className="font-mono-ops">{money(record.amount)}</span></div>
          {!due && <div data-testid="release-not-due" className="text-amber-300 text-xs">This check can be released on or after its date.</div>}
        </div>
        <Fld label="Deduct from (confirm it is funded)">
          <Select value={accId} onValueChange={setAccId}>
            <SelectTrigger data-testid="select-release-account" className={inputCls}><SelectValue placeholder="Choose cash or bank account" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type === "cash" ? "Cash" : "Bank"}) · {a.latest ? `bal ${money(a.latest.closing)}` : "no cash book yet"}</SelectItem>)}
            </SelectContent>
          </Select>
        </Fld>
        <DialogFooter>
          <Button data-testid="button-confirm-release" onClick={release} disabled={!accId || !due} className="bg-violet-600 hover:bg-violet-500">Release and deduct</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
